/**
 * TopTribunalsChart
 * Gráfico de barras mostrando os tribunais com mais publicações
 *
 * Fase 2.4 do Plano de Evolução - Top Tribunais/Processos
 */

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { Scale, TrendingUp, FileText } from 'lucide-react'
import {
  ChartTooltipBox,
  PainelCard,
  PainelEmpty,
  PainelStat,
  PainelStatRow,
  chartAxisProps,
  chartGridStroke
} from './PainelCard'

export interface TopTribunalData {
  tribunal: string
  count: number
  percentage: number
}

interface TopTribunalsChartProps {
  data: TopTribunalData[]
  className?: string
  limit?: number
}

// Líder na cor de destaque (chart-1); demais na escala neutra do tema
const COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)']

type TooltipProps = { active?: boolean; payload?: Array<{ payload: TopTribunalData }> }

function TribunalTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <ChartTooltipBox>
      <p className="mb-2 font-semibold">{item.tribunal}</p>
      <p>
        <span className="font-medium">Publicações:</span> {item.count}
      </p>
      <p className="font-medium text-primary">{item.percentage.toFixed(1)}% do total</p>
    </ChartTooltipBox>
  )
}

export function TopTribunalsChart({ data, className = '', limit = 5 }: TopTribunalsChartProps) {
  if (!data || data.length === 0) {
    return (
      <PainelCard title={`Top ${limit} Tribunais`} icon={Scale} className={className}>
        <PainelEmpty icon={Scale}>Nenhum dado disponível</PainelEmpty>
      </PainelCard>
    )
  }

  const totalPublications = data.reduce((sum, item) => sum + item.count, 0)
  const leader = data[0]

  return (
    <PainelCard
      title={`Top ${Math.min(limit, data.length)} Tribunais`}
      icon={Scale}
      className={className}
      action={
        <>
          <FileText className="size-3" aria-hidden="true" />
          <span>{totalPublications} publicações</span>
        </>
      }
    >
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={true} vertical={false} />
          <XAxis type="number" {...chartAxisProps} />
          <YAxis type="category" dataKey="tribunal" width={80} {...chartAxisProps} />
          <Tooltip content={<TribunalTooltip />} cursor={{ fill: 'var(--muted)' }} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${entry.tribunal}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <PainelStatRow className="grid-cols-3 md:grid-cols-3">
        <PainelStat label="Total" value={totalPublications} />
        <PainelStat label="Tribunais" value={data.length} />
        <PainelStat label="Líder" value={`${leader.percentage.toFixed(0)}%`} tone="primary" />
      </PainelStatRow>

      <div className="rounded-lg border border-border bg-muted/50 p-3">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <TrendingUp className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <span>Tribunal com mais publicações:</span>
        </div>
        <p className="mt-1 break-words text-lg font-semibold text-foreground">
          {leader.tribunal} ({leader.count} publicações)
        </p>
      </div>
    </PainelCard>
  )
}
